import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { uploadAttachmentViaTesoro } from '../../../lib/uploadAttachment';
export function ExternalOutputUpload({ captureProjectId, zouProjectId, templateId, disabled, onAttach }) {
    const { user } = useAuth();
    const storageKey = `capture-output-upload:${user?.id}:${captureProjectId}:${templateId}`;
    return /*#__PURE__*/ _jsx(UploadComposer, {
        zouProjectId,
        disabled,
        onAttach,
        storageKey
    }, storageKey);
}
function UploadComposer({ zouProjectId, storageKey, disabled, onAttach }) {
    const { user } = useAuth();
    const [uploaded, setUploaded] = useState(()=>{
        try {
            return JSON.parse(sessionStorage.getItem(storageKey) || 'null') || undefined;
        } catch  {
            return undefined;
        }
    });
    const [file, setFile] = useState();
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    async function upload() {
        if (inFlight.current || disabled || !user?.tenant_id) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            let result = uploaded;
            if (!result) {
                if (!file) return;
                const out = await uploadAttachmentViaTesoro(file, {
                    tenantId: user.tenant_id,
                    ownerUserId: user.id,
                    projectId: zouProjectId,
                    autoProcess: false,
                    onProgress: setProgress
                });
                result = {
                    artifactId: out.artifactId,
                    filename: out.filename
                };
                sessionStorage.setItem(storageKey, JSON.stringify(result));
                setUploaded(result);
            }
            await onAttach(result.artifactId);
            sessionStorage.removeItem(storageKey);
            setUploaded(undefined);
            setFile(undefined);
        } catch  {
            setError('Upload or attachment failed. The completed upload is retained for retry; refresh the current output if it changed.');
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 border-t pt-3",
        children: [
            /*#__PURE__*/ _jsx("h4", {
                children: "Externally authored final file · distinct output version"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "No structured parity. An uploaded file does not certify that structured sources or presentation overrides match its contents. Existing structured overrides remain recorded for later regeneration."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "External final file",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    type: "file",
                    "data-testid": Testids.captureOutputLifecycle.file,
                    disabled: disabled || busy || !!uploaded,
                    onChange: (event)=>setFile(event.target.files?.[0])
                })
            }),
            uploaded && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Upload completed: ",
                    uploaded.filename,
                    ". Retry attachment reuses these uploaded bytes."
                ]
            }),
            busy && /*#__PURE__*/ _jsxs("output", {
                children: [
                    "Uploading / recording external version… ",
                    progress,
                    "%"
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureOutputLifecycle.upload,
                disabled: disabled || busy || !file && !uploaded || !zouProjectId || !user?.tenant_id,
                onClick: upload,
                children: uploaded ? 'Retry attachment as new version' : 'Upload external final as new version'
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error
            })
        ]
    });
}
